import { supabase } from "./supabase";

/**
 * Thin client for Edge Function writes (API Conventions): every call sends
 * the `{ data, client_request_id }` envelope and unwraps the
 * `{ ok, data } / { ok:false, error }` response. Use this instead of writing
 * business-logic tables directly from the admin app — RLS on those tables is
 * deliberately read-only for interactive roles (money-math.md, security.md).
 */
export class EdgeFunctionError extends Error {
  code: string;
  details?: unknown;
  constructor(error: { code: string; message: string; details?: unknown }) {
    super(error.message);
    this.name = "EdgeFunctionError";
    this.code = error.code;
    this.details = error.details;
  }
}

interface EdgeSuccess<T> {
  ok: true;
  data: T;
}

interface EdgeFailure {
  ok: false;
  error: { code: string; message: string; details?: unknown };
}

async function authHeader(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not authenticated. Sign in again.");
  return `Bearer ${token}`;
}

/**
 * `path` is the function slug plus any sub-route, e.g. "admin/mileage/requests"
 * or "admin/audit?limit=50". GET requests carry no envelope; writes always do.
 */
export async function callEdgeFunction<T>(
  path: string,
  opts: { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; data?: unknown } = {},
): Promise<T> {
  const method = opts.method ?? "POST";
  const body =
    method === "GET"
      ? undefined
      : JSON.stringify({ data: opts.data ?? {}, client_request_id: crypto.randomUUID() });

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: await authHeader(),
    },
    body,
  });

  const json = (await res.json()) as EdgeSuccess<T> | EdgeFailure;
  if (!json.ok) throw new EdgeFunctionError(json.error);
  return json.data;
}
